import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import Form from "react-bootstrap/Form"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import MuiAlert from "@mui/material/Alert"
import DraftsIcon from "@mui/icons-material/Drafts"
import CancelIcon from "@mui/icons-material/Cancel"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import Accordion from "react-bootstrap/Accordion"
import Card from "react-bootstrap/Card"
import { adminURL } from "../../../../global"
import { checkRoute } from '../../../../services/auth';
import { fetchToken } from "../../../../Auth"
import axios from "axios"

const AlertMUI = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const CreateLearningMaterial = () => {
  const location = useLocation()
  const { privileges } = location.state || {}
  console.log(privileges, "_________-")

  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState("")
  const [pageURL, setPageURL] = useState("")
  const [seoTitle, setSeoTitle] = useState("")
  const [seoDescription, setSeoDescription] = useState("")
  const [materialState, setMaterialState] = useState("draft") // Added state field

  // Topics state
  const [topics, setTopics] = useState([])
  const [activeTopicIndex, setActiveTopicIndex] = useState(null)
  const [showNewTopicForm, setShowNewTopicForm] = useState(true)
  const [error, setError] = useState(null)
  // New topic form state
  const [newTopicName, setNewTopicName] = useState("")
  const [newTopicDesc, setNewTopicDesc] = useState("")
  const [newTopicLevel, setNewTopicLevel] = useState("")
  const [newTopicDomain, setNewTopicDomain] = useState("")
  const [newTopicTime, setNewTopicTime] = useState("")
  const [nextId, setNextId] = useState(null)
  // New subtopic form state
  const [newSubtopicName, setNewSubtopicName] = useState("")
  const [newSubtopicURL, setNewSubtopicURL] = useState("")

  // Add a new state for the confirmation dialog
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  // Add state for topic deletion confirmation
  const [deleteTopicDialog, setDeleteTopicDialog] = useState({
    open: false,
    topicIndex: null,
  })

  // Add state for Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info", // 'error', 'warning', 'info', 'success'
  })
  useEffect(() => {
    const verifyRoute = async () => {
      try {
        const response = await checkRoute(navigate, adminURL); 
        console.log('Route check response:', response);
      } catch (err) {
        console.log('Route check failed:', err);
      }
    };

    verifyRoute();
  }, [navigate, adminURL]);
  // Function to show snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  // Function to close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  // Handle title change and auto-generate URL
  const handleTitleChange = (e) => {
    const value = e.target.value
    setTitle(value)
    setPageURL(value.replace(/ /g, "-").toLowerCase())
  }

  // Add a new function to check for duplicate topic names
  const isDuplicateTopicName = (name, currentIndex = -1) => {
    return topics.some((topic, index) => topic.name.toLowerCase() === name.toLowerCase() && index !== currentIndex)
  }

  // Add a new function to check for duplicate subtopic names
  const isDuplicateSubtopicName = (topicIndex, name, currentIndex = -1) => {
    return topics[topicIndex].subtopics.some(
      (subtopic, index) => subtopic.name.toLowerCase() === name.toLowerCase() && index !== currentIndex,
    )
  }

  // Show new topic form
  const handleShowNewTopicForm = () => {
    setShowNewTopicForm(true)
    // Reset form fields
    setNewTopicName("")
    setNewTopicDesc("")
    setNewTopicLevel("")
    setNewTopicDomain("")
    setNewTopicTime("")
  }

  // Add a new function to handle the cancel button click
  const handleCancelClick = () => {
    setOpenConfirmDialog(true)
  }

  // Add a new topic
  const handleAddTopic = () => {
    // Validate all fields are filled
    if (!newTopicName.trim()) {
      showSnackbar("Topic name is required", "error")
      return
    }

    if (!newTopicDesc.trim()) {
      showSnackbar("Topic description is required", "error")
      return
    }

    if (!newTopicLevel) {
      showSnackbar("Topic level is required", "error")
      return
    }

    if (!newTopicDomain.trim()) {
      showSnackbar("Topic domain is required", "error")
      return
    }

    if (!newTopicTime) {
      showSnackbar("Estimated time is required", "error")
      return
    }

    // Check for duplicate topic name
    if (isDuplicateTopicName(newTopicName)) {
      showSnackbar("Topic name already exists. Please use a different name.", "error")
      return
    }

    const newTopic = {
      name: newTopicName,
      description: newTopicDesc,
      level: newTopicLevel,
      domain: newTopicDomain,
      time: newTopicTime,
      subtopics: [],
    }

    const updatedTopics = [...topics, newTopic]
    setTopics(updatedTopics)

    // Set the new topic as active
    const newTopicIndex = updatedTopics.length - 1
    setActiveTopicIndex(newTopicIndex.toString())

    // Hide the new topic form
    setShowNewTopicForm(false)

    // Reset form fields
    setNewTopicName("")
    setNewTopicDesc("")
    setNewTopicLevel("")
    setNewTopicDomain("")
    setNewTopicTime("")

    showSnackbar("Topic added successfully", "success")
  }

  // Cancel adding new topic
  const handleCancelAddTopic = () => {
    setShowNewTopicForm(false)
    // Reset form fields
    setNewTopicName("")
    setNewTopicDesc("")
    setNewTopicLevel("")
    setNewTopicDomain("")
    setNewTopicTime("")
  }

  // Update topic field
  const handleUpdateTopicField = (topicIndex, field, value) => {
    // Only check for duplicate topic name if the field being updated is 'name'
    if (field === "name" && isDuplicateTopicName(value, topicIndex)) {
      showSnackbar("Topic name already exists. Please use a different name.", "error")
      return
    }

    const updatedTopics = [...topics]
    updatedTopics[topicIndex] = {
      ...updatedTopics[topicIndex],
      [field]: value,
    }
    setTopics(updatedTopics)
  }

  // Add a subtopic to a topic
  const handleAddSubtopic = (topicIndex) => {
    // Validate all fields are filled
    if (!newSubtopicName.trim()) {
      showSnackbar("Subtopic name is required", "error")
      return
    }

    if (!newSubtopicURL.trim()) {
      showSnackbar("URL is required", "error")
      return
    }
    if (!newSubtopicURL.trim().startsWith("https")) {
      showSnackbar("URL must start with https", "error")
      return
    }

    if (isDuplicateSubtopicName(topicIndex, newSubtopicName)) {
      showSnackbar("Subtopic name already exists in this topic. Please use a different name.", "error")
      return
    }

    const updatedTopics = [...topics]
    updatedTopics[topicIndex].subtopics.push({
      name: newSubtopicName,
      url: newSubtopicURL,
    })

    setTopics(updatedTopics)

    // Reset form fields
    setNewSubtopicName("")
    setNewSubtopicURL("")

    showSnackbar("Subtopic added successfully", "success")
  }

  // Delete a subtopic
  const handleDeleteSubtopic = (topicIndex, subtopicIndex) => {
    const updatedTopics = [...topics]
    updatedTopics[topicIndex].subtopics.splice(subtopicIndex, 1)
    setTopics(updatedTopics)
  }

  // Move subtopic up in the list
  const handleMoveSubtopicUp = (topicIndex, subtopicIndex) => {
    if (subtopicIndex === 0) return // Already at the top

    const updatedTopics = [...topics]
    const subtopics = [...updatedTopics[topicIndex].subtopics]
    const temp = subtopics[subtopicIndex]
    subtopics[subtopicIndex] = subtopics[subtopicIndex - 1]
    subtopics[subtopicIndex - 1] = temp

    updatedTopics[topicIndex].subtopics = subtopics
    setTopics(updatedTopics)
  }

  // Move subtopic down in the list
  const handleMoveSubtopicDown = (topicIndex, subtopicIndex) => {
    const updatedTopics = [...topics]
    const subtopics = [...updatedTopics[topicIndex].subtopics]

    if (subtopicIndex === subtopics.length - 1) return // Already at the bottom

    const temp = subtopics[subtopicIndex]
    subtopics[subtopicIndex] = subtopics[subtopicIndex + 1]
    subtopics[subtopicIndex + 1] = temp

    updatedTopics[topicIndex].subtopics = subtopics
    setTopics(updatedTopics)
  }

  // Delete a topic - Modified to show confirmation dialog
  const handleDeleteTopic = (topicIndex) => {
    setDeleteTopicDialog({
      open: true,
      topicIndex: topicIndex,
    })
  }

  // Confirm topic deletion
  const handleConfirmDeleteTopic = () => {
    const updatedTopics = [...topics]
    updatedTopics.splice(deleteTopicDialog.topicIndex, 1)
    setTopics(updatedTopics)
    setDeleteTopicDialog({ open: false, topicIndex: null })
    showSnackbar("Topic deleted successfully", "success")
  }

  // Cancel topic deletion
  const handleCloseDeleteTopicDialog = () => {
    setDeleteTopicDialog({ open: false, topicIndex: null })
  }

  // Move topic up in the list
  const handleMoveTopicUp = (topicIndex) => {
    if (topicIndex === 0) return // Already at the top

    const updatedTopics = [...topics]
    const temp = updatedTopics[topicIndex]
    updatedTopics[topicIndex] = updatedTopics[topicIndex - 1]
    updatedTopics[topicIndex - 1] = temp

    setTopics(updatedTopics)
  }

  // Move topic down in the list
  const handleMoveTopicDown = (topicIndex) => {
    if (topicIndex === topics.length - 1) return // Already at the bottom

    const updatedTopics = [...topics]
    const temp = updatedTopics[topicIndex]
    updatedTopics[topicIndex] = updatedTopics[topicIndex + 1]
    updatedTopics[topicIndex + 1] = temp

    setTopics(updatedTopics)
  }

  // Handle accordion selection
  const handleAccordionSelect = (eventKey) => {
    setActiveTopicIndex(eventKey)
  }

  // Save as draft with updated payload structure and API call
  const handleSaveDraft = async () => {
    if (nextId === null) {
      alert("Failed to get next ID. Please refresh and try again.")
      return
    }
    // Validate main form fields
    if (!title.trim()) {
      showSnackbar("Title is required", "error")
      return
    }

    if (!pageURL.trim()) {
      showSnackbar("Page URL is required", "error")
      return
    }

    if (!seoTitle.trim()) {
      showSnackbar("SEO Title is required", "error")
      return
    }

    if (!seoDescription.trim()) {
      showSnackbar("SEO Description is required", "error")
      return
    }

    if (topics.length === 0) {
      showSnackbar("At least one topic is required", "error")
      return
    }

    // Validate that all topics have required fields
    for (let i = 0; i < topics.length; i++) {
      const topic = topics[i]

      if (!topic.name || !topic.name.trim()) {
        showSnackbar(`Topic #${i + 1} name is required`, "error")
        return
      }

      if (!topic.description || !topic.description.trim()) {
        showSnackbar(`Topic #${i + 1} description is required`, "error")
        return
      }

      if (!topic.level || !topic.level.trim()) {
        showSnackbar(`Topic #${i + 1} level is required`, "error")
        return
      }

      if (!topic.domain || !topic.domain.trim()) {
        showSnackbar(`Topic #${i + 1} domain is required`, "error")
        return
      }

      if (!topic.time) {
        showSnackbar(`Topic #${i + 1} estimated time is required`, "error")
        return
      }

      // Validate that all subtopics have required fields
      for (let j = 0; j < topic.subtopics.length; j++) {
        const subtopic = topic.subtopics[j]

        if (!subtopic.name || !subtopic.name.trim()) {
          showSnackbar(`Subtopic #${j + 1} in Topic "${topic.name}" name is required`, "error")
          return
        }

        if (!subtopic.url || !subtopic.url.trim()) {
          showSnackbar(`Subtopic #${j + 1} in Topic "${topic.name}" URL is required`, "error")
          return
        }
      }
    }

    // Set state to draft
    setMaterialState("draft")

    // Prepare data for submission with SEO fields grouped in an object
    const learningMaterial = {
      id: nextId + 1,
      title,
      isLive: false,
      topics,
      pageURL,
      seo: {
        seoTitle,
        seoDescription,
      },
    }

    console.log("Saving draft:", learningMaterial)
    setLoading(true)

    try {
      // Make the API call with JSON content type
      const response = await axios.post(
        "/backend/create_learning_material", // Replace with your actual API endpoint
        learningMaterial, // Send the JSON object directly
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        },
      )
      console.log("API Response:", response.data)
      showSnackbar("Draft saved successfully!", "success")
      setLoading(false)
      navigate(`/niyantran-panel/${adminURL}/hr-content/learningManagement`, { state: { privileges } })
    } catch (error) {
      if (error.response?.status === 401) {
        navigate(`/niyantran-panel/${adminURL}/login`)
      }
      console.error("Error saving draft:", error)
      showSnackbar(`${error.response.data.msg}`, "error")
      setLoading(false)
    }
  }

  // Add a function to handle dialog confirmation
  const handleConfirmCancel = () => {
    setOpenConfirmDialog(false)
    navigate(`/niyantran-panel/${adminURL}/hr-content/learningManagement`, {
      state: { privileges },
    })
  }

  // Add a function to handle dialog close/cancel
  const handleCloseDialog = () => {
    setOpenConfirmDialog(false)
  }

  useEffect(() => {
    fetchNextId()
  }, [])

  // Fetch the next ID from the API
  const fetchNextId = async () => {
    try {
      setLoading(true)
      const response = await axios.get("/backend/get_lm_max_id", {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      if (response.data) {
        setNextId(response.data.nextId)
      } else {
        setError("Failed to get next ID. Please try again.")
      }
    } catch (err) {
      console.error("Error fetching next ID:", err)
      setError("Failed to get next ID. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <CircularProgress />
        <span className="ml-3">Creating learning material...</span>
      </div>
    )
  }
  if (error) {
    return (
      <div className="alert alert-danger mt-2">
        {error}
        <div className="mt-3">
          <Button variant="contained" onClick={() =>  navigate(`/niyantran-panel/${adminURL}/hr-content/learningManagement`, {
        state: { privileges },
      })}>
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="container white-bg text-center shadow p-2 rounded mt-2">
        <div className="d-flex justify-content-between align-items-center">
          <Button variant="contained" onClick={handleCancelClick}>
            Go Back
          </Button>
          <h1 className="fw-bolder">Create Learning Material</h1>
          <div style={{ width: "85px" }}></div> {/* This creates balance in the header */}
        </div>
      </div>

      <div className="row mt-4">
        {/* Main Content Column */}
        <div className="col-md-8 d-flex flex-column gap-3">
          {/* Title Section */}
          <div className="white-bg rounded py-2 px-3 shadow-sm">
            <Form.Group className="mb-3" controlId="title">
              <Form.Label className="med-font ml-1">Title*</Form.Label>
              <Form.Control type="text" value={title} onChange={handleTitleChange} />
            </Form.Group>
          </div>

          {/* Topics Section */}
          <div className="white-bg rounded py-2 px-3 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Label className="med-font ml-1 mb-0">Topics</Form.Label>
              {!showNewTopicForm && (
                <Button variant="contained" onClick={handleShowNewTopicForm} className="px-3">
                  + Topics
                </Button>
              )}
            </div>

            {/* New Topic Form */}
            {showNewTopicForm && (
              <div className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Add New Topic</h6>
                  {topics.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCancelAddTopic}
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Topic Name*</Form.Label>
                  <Form.Control type="text" value={newTopicName} onChange={(e) => setNewTopicName(e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Topic Description*</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={newTopicDesc}
                    onChange={(e) => setNewTopicDesc(e.target.value)}
                  />
                </Form.Group>
                <div className="row">
                  <div className="col-md-4">
                    <Form.Group className="mb-3">
                      <Form.Label>Select Level*</Form.Label>
                      <Form.Select value={newTopicLevel} onChange={(e) => setNewTopicLevel(e.target.value)}>
                        <option value="">Select Level</option>
                        <option value="basic">Basic</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-3">
                      <Form.Label>Domain*</Form.Label>
                      <Form.Control
                        type="text"
                        value={newTopicDomain}
                        onChange={(e) => setNewTopicDomain(e.target.value)}
                        placeholder="e.g. Tech, Manager, Hr"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-3">
                      <Form.Label>Estimated Time*</Form.Label>
                      <Form.Control
                        type="text"
                        value={newTopicTime}
                        onChange={(e) => setNewTopicTime(e.target.value)}
                        placeholder="e.g. 2 Hrs/ Days/ Month"
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <Button variant="contained" onClick={handleAddTopic}>
                    Add Topic
                  </Button>
                </div>
                <div className="mt-3">
                  <p className="text-muted">After adding the topic, you'll be able to add subtopics immediately.</p>
                </div>
              </div>
            )}

            {/* Topics Accordion */}
            {topics.length > 0 && (
              <Accordion activeKey={activeTopicIndex} onSelect={handleAccordionSelect}>
                {topics.map((topic, topicIndex) => (
                  <Card key={topicIndex} className="mb-2">
                    <Accordion.Item eventKey={topicIndex.toString()}>
                      <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100 pe-4">
                          <Form.Control
                            type="text"
                            value={topic.name}
                            onChange={(e) => handleUpdateTopicField(topicIndex, "name", e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-50"
                          />
                          <div>
                            {topicIndex > 0 && (
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleMoveTopicUp(topicIndex)
                                }}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </Button>
                            )}
                            {topicIndex < topics.length - 1 && (
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleMoveTopicDown(topicIndex)
                                }}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteTopic(topicIndex)
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        {/* Topic Form - Always Editable */}
                        <div className="border rounded p-3 mb-3">
                          <h6 className="mb-3">Topic Details</h6>
                          <Form.Group className="mb-3">
                            <Form.Label>Topic Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={topic.description}
                              onChange={(e) => handleUpdateTopicField(topicIndex, "description", e.target.value)}
                            />
                          </Form.Group>
                          <div className="row">
                            <div className="col-md-4">
                              <Form.Group className="mb-3">
                                <Form.Label>Select Level</Form.Label>
                                <Form.Select
                                  value={topic.level}
                                  onChange={(e) => handleUpdateTopicField(topicIndex, "level", e.target.value)}
                                >
                                  <option value="">Select Level</option>
                                  <option value="basic">Basic</option>
                                  <option value="intermediate">Intermediate</option>
                                  <option value="advanced">Advanced</option>
                                </Form.Select>
                              </Form.Group>
                            </div>
                            <div className="col-md-4">
                              <Form.Group className="mb-3">
                                <Form.Label>Domain</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={topic.domain}
                                  onChange={(e) => handleUpdateTopicField(topicIndex, "domain", e.target.value)}
                                  placeholder="e.g., Tech, Hr, Manager"
                                />
                              </Form.Group>
                            </div>
                            <div className="col-md-4">
                              <Form.Group className="mb-3">
                                <Form.Label>Estimated Time (mins)</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={topic.time}
                                  onChange={(e) => handleUpdateTopicField(topicIndex, "time", e.target.value)}
                                  placeholder="e.g., 30 min"
                                />
                              </Form.Group>
                            </div>
                          </div>
                        </div>

                        {/* Add Subtopic Form */}
                        <div className="border rounded p-3 mb-3">
                          <h6>Add Subtopic</h6>
                          <div className="row">
                            <div className="col-md-5">
                              <Form.Group className="mb-3">
                                <Form.Label>Subtopic Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={newSubtopicName}
                                  onChange={(e) => setNewSubtopicName(e.target.value)}
                                />
                              </Form.Group>
                            </div>
                            <div className="col-md-5">
                              <Form.Group className="mb-3">
                                <Form.Label>URL</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={newSubtopicURL}
                                  onChange={(e) => setNewSubtopicURL(e.target.value)}
                                />
                              </Form.Group>
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                              <Button
                                variant="contained"
                                className="mb-3"
                                onClick={() => handleAddSubtopic(topicIndex)}
                              >
                                Add
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Subtopics Table */}
                        {topic.subtopics.length > 0 && (
                          <Table className="mt-2 text-center">
                            <TableHead>
                              <TableRow>
                                <TableCell>Topic Name</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {topic.subtopics.map((subtopic, subtopicIndex) => (
                                <TableRow key={subtopicIndex}>
                                  <TableCell>{subtopic.name}</TableCell>
                                  <TableCell>{subtopic.url}</TableCell>
                                  <TableCell>
                                    <div className="d-flex justify-content-center gap-2">
                                      {subtopicIndex > 0 && (
                                        <ArrowUpwardIcon
                                          fontSize="small"
                                          sx={{ cursor: "pointer", color: "#1976d2" }}
                                          onClick={() => handleMoveSubtopicUp(topicIndex, subtopicIndex)}
                                        />
                                      )}
                                      {subtopicIndex < topic.subtopics.length - 1 && (
                                        <ArrowDownwardIcon
                                          fontSize="small"
                                          sx={{ cursor: "pointer", color: "#1976d2" }}
                                          onClick={() => handleMoveSubtopicDown(topicIndex, subtopicIndex)}
                                        />
                                      )}
                                      <DeleteIcon
                                        onClick={() => handleDeleteSubtopic(topicIndex, subtopicIndex)}
                                        sx={{ color: "#ff3333", cursor: "pointer" }}
                                      />
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Card>
                ))}
              </Accordion>
            )}
          </div>
        </div>

        {/* Sidebar Column */}
        <div className="col-md-4 d-flex flex-column gap-3">
          <div className="grey-blue-bg rounded py-2 px-3 shadow-sm">
            <Form.Group className="mb-3">
              <Form.Label className="med-font ml-1">SEO Details</Form.Label>
              <Form.Control
                type="text"
                placeholder="SEO Title"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                className="mb-2"
              />
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="SEO Description"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="grey-blue-bg rounded py-2 px-3 shadow-sm">
            <Form.Group className="mb-3">
              <Form.Label className="med-font ml-1">Page URL</Form.Label>
              <Tooltip title="The URL where this learning material will be accessible" arrow placement="right">
                <i className="fa-solid fa-circle-info ml-2 color-grey"></i>
              </Tooltip>
              <Form.Control type="text" value={pageURL} onChange={(e) => setPageURL(e.target.value)} />
            </Form.Group>
          </div>

          {/* Action Buttons */}
          <div className="grey-blue-bg rounded d-flex justify-content-center gap-4 p-4 shadow-sm mt-3">
            <Button variant="contained" startIcon={<DraftsIcon />} onClick={handleSaveDraft}>
              Save Draft
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Navigation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to go back? Any unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Stay on Page</Button>
          <Button onClick={handleConfirmCancel} autoFocus>
            Go Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Topic Deletion Confirmation Dialog */}
      <Dialog
        open={deleteTopicDialog.open}
        onClose={handleCloseDeleteTopicDialog}
        aria-labelledby="delete-topic-dialog-title"
        aria-describedby="delete-topic-dialog-description"
      >
        <DialogTitle id="delete-topic-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-topic-dialog-description">
            Are you sure you want to delete this topic? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteTopicDialog}>Cancel</Button>
          <Button onClick={handleConfirmDeleteTopic} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default CreateLearningMaterial

